import { Link } from "react-router-dom";
import { useEffect } from "react";
import { trackViewContent } from "./lib/pixel";

/**
 * Interiors home — rediseño 2026-09-28. Editorial cálido NO-beige: olive/forest +
 * bone + amber, Cormorant Garamond display, Hanken UI, AI renders de calidad AD.
 * Estructura "ver el trabajo -> agendar". Sin emojis, un solo acento (ámbar),
 * un solo intent de CTA ("Book a consultation").
 */
export default function App() {
  useEffect(() => trackViewContent("home"), []);
  return (
    <main className="bg-bone text-ink">
      {/* Nav */}
      <header className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 py-6 md:px-10">
        <span className="font-serif text-2xl tracking-wide text-bone drop-shadow">A Couple &amp; A Dream</span>
        <Link
          to="/quiz"
          className="rounded-full border border-bone/45 px-6 py-2.5 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bone transition hover:border-clay hover:bg-clay hover:text-ink"
        >
          Book a consultation
        </Link>
      </header>

      {/* Hero */}
      <section className="relative flex min-h-[100dvh] items-end overflow-hidden">
        <img src="/img/renders/hero-living.png" alt="A living room designed and built by A Couple and A Dream" className="absolute inset-0 h-full w-full object-cover" />
        <span className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-forest/75 to-transparent" />
        <span className="absolute inset-0 bg-gradient-to-t from-forest/92 via-forest/25 to-transparent" />
        <div className="relative z-10 w-full px-6 pb-16 md:px-10 md:pb-24">
          <div className="max-w-3xl text-bone">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-bone/75">Interior design and build · Tampa Bay</p>
            <h1 className="mt-4 font-serif text-6xl font-medium leading-[1.0] md:text-8xl">
              A home that feels like <span className="italic text-clay">you</span>, designed and built by one couple.
            </h1>
            <p className="mt-6 max-w-lg font-sans text-lg leading-relaxed text-bone/85">
              Ana Clara and Javier design your home and build it themselves. One accountable team, from first sketch to the final wall. We take only a few homes a month.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
              <Link to="/quiz" className="rounded-full bg-clay px-9 py-4 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-ink transition hover:bg-bone active:scale-[0.98]">
                Book a consultation
              </Link>
              <a href="#work" className="font-sans text-sm uppercase tracking-[0.16em] text-bone/80 underline-offset-4 hover:underline">See the work</a>
            </div>
          </div>
        </div>
      </section>

      {/* The work — asymmetric editorial grid */}
      <section id="work" className="px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <h2 className="font-serif text-5xl leading-[1.05] md:text-6xl">The look we design <span className="italic text-clay">toward</span>.</h2>
            <p className="mt-5 max-w-lg font-sans text-stone">One team designs your home and builds it, from first sketch to the final wall. The rooms below are design renderings that show our direction, not photos of past projects.</p>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12">
            <figure className="md:col-span-7">
              <img src="/img/renders/bath.png" alt="A primary bathroom" className="aspect-[4/5] w-full rounded-sm object-cover md:aspect-[7/6]" />
              <figcaption className="mt-3 font-sans text-[11px] uppercase tracking-[0.16em] text-stone">Primary bath · Design rendering</figcaption>
            </figure>
            <figure className="md:col-span-5 md:pt-20">
              <img src="/img/renders/kitchen.png" alt="A kitchen" className="aspect-[4/5] w-full rounded-sm object-cover" />
              <figcaption className="mt-3 font-sans text-[11px] uppercase tracking-[0.16em] text-stone">Kitchen · Design rendering</figcaption>
            </figure>
            <figure className="md:col-span-6 md:col-start-4">
              <img src="/img/renders/suite.png" alt="A primary suite" className="aspect-[16/11] w-full rounded-sm object-cover" />
              <figcaption className="mt-3 font-sans text-[11px] uppercase tracking-[0.16em] text-stone">Primary suite · Design rendering</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Moat — deep olive color block */}
      <section className="bg-forest px-6 py-24 text-bone md:px-10 md:py-32">
        <div className="mx-auto max-w-5xl">
          <h2 className="max-w-2xl font-serif text-5xl leading-[1.05] md:text-6xl">One team. Designed <span className="italic text-clay">and</span> built.</h2>
          <div className="mt-16 grid grid-cols-1 gap-x-12 gap-y-14 md:grid-cols-3">
            {[
              ["Design leads the build", "Layout, light and materials are decided before a wall moves, not chosen from samples at the end."],
              ["Nothing lost in translation", "No designer on one side and a separate crew on the other. The people who draw it, build it."],
              ["You are not the middleman", "From the first sketch to the final walk-through, it is Ana Clara and Javier."],
            ].map(([h, p]) => (
              <div key={h}>
                <h3 className="font-serif text-3xl leading-snug">{h}</h3>
                <p className="mt-3 max-w-xs font-sans text-sm leading-relaxed text-bone/70">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation band */}
      <section className="px-6 py-24 text-center md:px-10 md:py-32">
        <div className="mx-auto max-w-2xl">
          <h2 className="font-serif text-5xl leading-[1.05] md:text-6xl">Your consultation is with the couple who designs and builds it.</h2>
          <p className="mx-auto mt-6 max-w-md font-sans text-stone">
            Ana Clara and Javier walk your home, listen to how you live, and turn it into a real, buildable plan. We take only a few homes a month.
          </p>
          <Link to="/quiz" className="mt-9 inline-block rounded-full bg-clay px-10 py-4 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-ink transition hover:bg-ink hover:text-bone active:scale-[0.98]">
            Book a consultation
          </Link>
        </div>
      </section>

      <footer className="border-t border-stone/20 px-6 py-8 text-center font-sans text-xs text-stone md:px-10">
        <p>A Couple &amp; A Dream · Interior design and build in Tampa Bay</p>
        <p className="mt-2">
          <Link to="/privacy" className="hover:text-ink">Privacy</Link> · <Link to="/terms" className="hover:text-ink">Terms</Link>
        </p>
      </footer>
    </main>
  );
}
